import { gql } from 'graphql-request';
import { For, Show, createEffect, createSignal, on, useContext } from 'solid-js';
import { AppContext } from '../../../../app-context-provider/app-context-provider';
import { Button } from '../../../../ui-components/button/button';
import { Text } from '../../../../ui-components/text/text';
import { StyledFlexRow } from '../../../../ui-components/utility-style-components/flex';
import { StyledVerticalSpace } from '../../../../ui-components/utility-style-components/spacing';
import { Collection, pdfStore, setPdfStore } from '../../pdf-store';
import { StyledListItemForPdf, StyledLoadingSpinnerContainer, StyledOutput, StyledCollection, StyledExpandableContainerContents } from './collection-single.style';
import theme from '../../../../style/theme';
import { LoadingSpinner } from '../../../loading-spinner/loading-spinner';
import { incrementUserPdfPrintCount } from '../pdf-newsletter-signup-prompt/newsletter-prompt-checker';
import removeAmpersand from '../../../../tools/remove-ampersand';
import { DeleteIcon } from '../../../icons-library/delete';
import { ExpandableContainer } from '../../../../ui-components/expandable-container/expandable-container';

const GET_PDF_QUERY = gql`
    query GeneratePdfFile (
        $domain: String!,
        $environment: String!,
        $postKeys: [String],
    ) {
        resource: generatePdfFile(
            domain: $domain, 
            environment: $environment,
            postKeys: $postKeys,
        ) {
            buffer, 
            success,
            message,
            maxConcurrent,
        }
    }
`;

const maxRetryAttempts = 2;

type CollectionSingleProps = {
    collection: Collection;
    deleteCollection: (collectionId: string) => void;
    editCollection: () => void;
    reset: () => void;
    isExpanded: boolean;
    expandTimestamp?: number;
};

export const CollectionSingle = (props: CollectionSingleProps) => {
    const { 
        graphQlClient,
        deploymentEnvironment,
        localize,
        viewport,
    } = useContext(AppContext);

    const [ forcedOpen, setForcedOpen ] = createSignal(false);
    const [ forcedClose, setForcedClose ] = createSignal(false);
    const [ isDeleting, setIsDeleting ] = createSignal(false);
    const [ generationRetryCount, setGenerationRetryCount ] = createSignal(0);

    let expandableContainer: HTMLDivElement | undefined;

    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    const isActiveCollection = () => pdfStore.activeCollectionId === props.collection.id;

    const busy = () => {
        // Disables interactivity while the PDF file generator is busy
        return (
            pdfStore.status === 'generating'
            || pdfStore.status === 'retrying-generating'
            || pdfStore.status === 'checking-availability'
            || pdfStore.status === 'creating'
            || pdfStore.status === 'deleting'
            || pdfStore.status === 'updating-existing'
            || pdfStore.status === 'editing'
            || pdfStore.activeCollectionId !== ''
        );
    };

    createEffect(
        on(
            // The timestamp is used to force re-render when expanding/collapsing
            [() => props.isExpanded, () => props.expandTimestamp],
            () => {                
                if (props.isExpanded) { 
                    setForcedOpen(true);
                    setForcedClose(false);
                } else {
                    setForcedOpen(false);
                    setForcedClose(true);
                }
            },
            { defer: true }
        ),
    );

    createEffect(
        on(
            [() => pdfStore.status, () => pdfStore.activeCollectionId],
            () => {
                if (pdfStore.status === 'editing' && pdfStore.activeCollectionId) {
                    // When editing, collapse all other collections
                    if (pdfStore.activeCollectionId !== props.collection.id) {
                        setForcedOpen(false);
                        setForcedClose(true);
                    }
                }
            }
        )
    );

    const requestFile = async (collection: Collection, retryCount: number = 0) => {
        try {
            setPdfStore('activeCollectionId', collection.id);
            setPdfStore('collections', col => col.title === collection.title, 'downloaded', false);
            setPdfStore('status', 'generating');
            setGenerationRetryCount(retryCount);
            setPdfStore('output', {
                id: collection.id,
                blob: ''
            });
            
            const postKeys = collection.orderedPosts.map((item) => item.key);
            const file = await fetchFile(postKeys, collection);

            if (!file) {
                return;
            }
            
            setPdfStore('output', 'blob', file);
            setPdfStore('collections', col => col.title === collection.title, 'downloaded', true);

            props.reset();

            triggerDownload(collection.title, file);
        } catch (err: any) {
            console.log('PDF file request error: ', err);
            setPdfStore('status', 'error');
            return;
        }
    };

    const fetchFile = async (postKeys: string[], collection: Collection) => {
        try {
            const variables = {
                domain: collection.siteInfo.domain,
                environment: deploymentEnvironment,
                postKeys,
            };
    
            const response = await graphQlClient(GET_PDF_QUERY, variables, false);
    
            if (!response || !response.resource) {
                throw new Error('No response from server');
            }
    
            const retryAttemptsReached = generationRetryCount() >= maxRetryAttempts;
            
            if (response.resource.maxConcurrent && !retryAttemptsReached) {
                // Retry download twice if the PDF service is busy
                setPdfStore('status', 'retrying-generating');
                const count = generationRetryCount() + 1;
                setGenerationRetryCount(count);
    
                setTimeout(() => {
                    requestFile(collection, count);
                }, 8000);
    
                return;
            }            
    
            if (response.resource.maxConcurrent && retryAttemptsReached) {
                setPdfStore('status', 'server-busy');
                return;
            }
    
            if (!response.resource.success) {                
                throw new Error(response.resource.message);
            }
    
            return response.resource.buffer;
        } catch (err: any) {
            console.log('PDF fetch file error: ', err);
            setPdfStore('status', 'error');
            return;
        }
    };

    const triggerDownload = (title: string, blob: string) => {
        const link = document.createElement('a');
        link.href = `data:application/octet-stream;base64,${blob}`;
        link.download = `${title}.pdf`;
        link.click();
    };

    const PostsList = () => (
        <For each={props.collection.orderedPosts}>{(item) => (
            <StyledListItemForPdf>
                <Text
                    noBlockSpacing={true}
                    fontSize='small'
                    displayRedVerticalLine={false}
                    color={pdfStore.status === 'generating'
                        ? isActiveCollection() ? 'darkestGray' : 'lightGray'
                        : 'darkestGray'
                    }
                >
                    {removeAmpersand(item.postTitle)}
                </Text>
            </StyledListItemForPdf>
        )}</For>
    );

    const DeleteButtons = () => (
        <StyledFlexRow gap='1rem' alignItems='center'>
            <Show
                when={isDeleting()}
                fallback={
                    <button onClick={() => setIsDeleting(true)} disabled={busy()} >
                        <DeleteIcon width={2} height={2} stroke={busy() ? theme.palette.lightGray : theme.palette.red} />
                    </button>
                }
            >
                <Button
                    label={localize('pdf-delete-collection', 'Delete')}
                    variant='tertiary'
                    small={true}
                    onClick={() => {
                        props.deleteCollection(props.collection.id);
                        setIsDeleting(false);
                    }}
                    disabled={busy()}
                />
                <Button
                    label={localize('pdf-cancel-delete', 'Cancel')}
                    variant='tertiary'
                    small={true}
                    onClick={() => setIsDeleting(false)}
                    disabled={busy()}
                />

            </Show>
        </StyledFlexRow>
    );

    const EditButton = () => (
        <Button
            label={localize('pdf-collection-edit', 'Edit')}
            disabled={busy()}
            variant='secondary'
            small={true}
            onClick={() => {
                props.editCollection();
            }}
        />
    );

    const DownloadButton = () => (
        <Button
            label={props.collection.downloaded
                ? localize('pdf-download-again', 'Download again')
                : localize('pdf-download', 'Download')
            }
            disabled={busy()}
            variant='primary'
            small={true}
            arrow={true}
            onClick={() => {
                incrementUserPdfPrintCount();
                requestFile(props.collection);
            }}
        />
    );

    const StatusGenerating = () => (
        <Show when={pdfStore.status === 'generating' && isActiveCollection()}>
            <StyledOutput withLoadingSpinner={true} bgColor={'#F7F7F7'}>
                <Show when={!pdfStore.output.blob}>
                    <StyledLoadingSpinnerContainer>
                        <LoadingSpinner size='2.2rem' />
                    </StyledLoadingSpinnerContainer>

                    <Text fontSize='small'>
                        {localize(
                            'pdf-generating-file',
                            'Generating file, download will occur once complete'
                        )}
                    </Text>
                </Show>
            </StyledOutput>
        </Show>
    );

    const StatusSaving = () => (
        <Show when={pdfStore.status === 'updating-existing' && isActiveCollection()}>
            <StyledOutput withLoadingSpinner={true} bgColor='#F7F7F7' >
                <StyledLoadingSpinnerContainer>
                    <LoadingSpinner size='2.2rem' />
                </StyledLoadingSpinnerContainer>
                <Text fontSize='small'>
                    {localize(
                        'pdf-saving-collection-status',
                        'Saving, please wait...'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const StatusDeleting = () => (
        <Show when={pdfStore.status === 'deleting' && isActiveCollection()}>
            <StyledOutput withLoadingSpinner={true} bgColor='#F7F7F7' >
                <StyledLoadingSpinnerContainer>
                    <LoadingSpinner size='2.2rem' />
                </StyledLoadingSpinnerContainer>
                <Text fontSize='small'>
                    {localize(
                        'pdf-deleting-collection-status',
                        'Deleting collection, please wait...'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const StatusEditing = () => (
        <Show when={pdfStore.status === 'editing' && isActiveCollection()}>
            <StyledOutput withLoadingSpinner={true} bgColor={'#F7F7F7'} >
                <div>
                    <Text fontSize='small' noBlockSpacing>
                        {localize(
                            'pdf-editing-collection-status',
                            'Editing collection'
                        )}
                    </Text>

                    <Button
                        label={localize('pdf-cancel-edit', 'Cancel edit')}
                        variant='tertiary'
                        small={true}
                        onClick={() => props.reset()}
                    />
                </div>
            </StyledOutput>
        </Show>
    );

    const StatusRetryingGenerating = () => (
        <Show when={pdfStore.status === 'retrying-generating' && isActiveCollection()}>
            <StyledOutput>
                <Text fontSize='small'>{
                    `${localize(
                        'pdf-file-generator-busy-retrying',
                        'File generator busy, retrying in a few seconds'
                    )} ${localize(
                        'pdf-file-generator-attempt',
                        'Attempt'
                    )} ${generationRetryCount()} / ${maxRetryAttempts}`
                }</Text>
            </StyledOutput>
        </Show>
    );

    const StatusBusy = () => (
        <Show when={pdfStore.status === 'server-busy' && isActiveCollection()}>
            <StyledOutput bgColor='#F7F7F7'>
                <Text fontSize='small'>
                    {localize(
                        'pdf-file-generator-at-capacity',
                        'File generator at capacity, please try again later'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const StatusError = () => (
        <Show when={pdfStore.status === 'error' && isActiveCollection()}>
            <StyledOutput bgColor={theme.palette.lighterPink}>
                <Text fontSize='small'>
                    {localize(
                        'pdf-download-error',
                        'An error has occurred, please try again later or contact support if the problem persists.'
                    )}
                </Text>
            </StyledOutput>
        </Show>
    );

    const StatusMessages = () => (
        <>
            <StatusGenerating />

            <StatusSaving />

            <StatusDeleting />
     
            <StatusEditing />

            <StatusRetryingGenerating />

            <StatusBusy />

            <StatusError />
        </>
    );

    return (
        <StyledCollection active={isActiveCollection()}>
            <ExpandableContainer
                initialOpen={false}
                chevronColor={theme.palette.red}
                chevronSize={isMobile() ? 1.5 : 2.14}
                roundCorners={true}
                ref={expandableContainer}
                forceClose={forcedClose()}
                forceOpen={forcedOpen()}
                forceTimestamp={props.expandTimestamp}
                disableOutsideClickToClose={true}
                backgroundColor={!busy() || isActiveCollection() ? theme.palette.white : '#F7F7F7'}
                headingProps={{
                    noBlockSpacing: true,
                    tag: 'h4',
                    variant: 'tinyGray',
                    children: props.collection.title,
                }}
                customHeadingRowCss={`
                    background-color: ${!busy() || isActiveCollection() ? theme.palette.white : '#F7F7F7'};
                    padding-block: ${theme.spacing.s};
                    border-top-left-radius: ${theme.borderRadius.medium};
                    border-top-right-radius: ${theme.borderRadius.medium};
                    padding-inline: ${isMobile() ? theme.spacing.xs : theme.spacing.ms};
                `}
            >
                <StyledExpandableContainerContents bgColor={!busy() || isActiveCollection() ? theme.palette.white : '#F7F7F7'}>

                    <PostsList />

                    <StyledVerticalSpace size={ 1 } />

                    <StyledFlexRow justifyContent='end' flexWrap='wrap' columnGap={'1.25rem'}>
                        <DeleteButtons />
                        <EditButton />
                        <DownloadButton />
                    </StyledFlexRow>

                    <StatusMessages />

                </StyledExpandableContainerContents>
            </ExpandableContainer>
        </StyledCollection>
    );
};
